// ─────────────────────────────────────────────────────────────────
// V2Dashboard — Tabbed overlay panel for all V2 platform milestones
//
// Renders as a full-screen overlay (same pattern as TemplateLibrary).
// Each tab corresponds to a milestone (M4–M8.7).
// Data comes from v2ApiService (mock); swap to real calls later.
//
// Integration:  Frame.js renders <V2Dashboard /> alongside
//               <TemplateLibrary />.  Header.js toggles visibility
//               via Redux OPEN/CLOSE actions.
// ─────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    closeV2Dashboard,
    openV2Dashboard,
} from '../../redux/actions/v2DashboardActions';
import * as api from '../services/v2ApiService';
import '../styles/v2-dashboard.css';

const TABS = [
    { id: 'overview',  label: 'Overview',         icon: '🏠' },
    { id: 'm4',        label: 'API Docs',          icon: '📘' },
    { id: 'm5',        label: 'Versioning',        icon: '🔄' },
    { id: 'm6',        label: 'SDKs',              icon: '📦' },
    { id: 'm7',        label: 'Signed PDF',        icon: '🔏' },
    { id: 'm8',        label: 'Audit Log',         icon: '📋' },
    { id: 'm8_5',      label: 'Storage',           icon: '💾' },
    { id: 'm8_7',      label: 'Team & Invite',     icon: '👥' },
];

// ─── Small reusable bits ─────────────────────────────────────────
const Badge = ({ children, variant = 'default' }) => (
    <span className={`v2-badge v2-badge-${variant}`}>{children}</span>
);

const MethodBadge = ({ method }) => {
    const m = (method || '').toUpperCase();
    const cls = m === 'GET' ? 'get' : m === 'POST' ? 'post' : m === 'PUT' ? 'put' : m === 'DELETE' ? 'delete' : 'default';
    return <span className={`v2-method v2-method-${cls}`}>{m}</span>;
};

const ProgressBar = ({ value, max, label }) => (
    <div className="v2-progress-wrap">
        {label && <span className="v2-progress-label">{label}</span>}
        <div className="v2-progress-bar">
            <div className="v2-progress-fill" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
        </div>
        <span className="v2-progress-text">{value.toLocaleString()} / {max == null ? '∞' : max.toLocaleString()}</span>
    </div>
);

const SectionTitle = ({ children }) => <h3 className="v2-section-title">{children}</h3>;

// ─── Overview Panel ──────────────────────────────────────────────
const OverviewPanel = ({ account, usage }) => {
    if (!account || !usage) return <div className="v2-loading">Loading...</div>;
    return (
        <div className="v2-panel">
            <SectionTitle>Account Overview</SectionTitle>
            <div className="v2-card v2-card-accent">
                <div className="v2-card-row"><strong>Account:</strong> {account.accountName}</div>
                <div className="v2-card-row"><strong>Plan:</strong> <Badge variant="plan">{account.plan}</Badge></div>
                <div className="v2-card-row"><strong>Role:</strong> {account.role}</div>
                <div className="v2-card-row"><strong>Email:</strong> {account.email}</div>
            </div>

            <SectionTitle>Usage This Month</SectionTitle>
            <ProgressBar value={usage.pdfsGenerated} max={usage.pdfsLimit} label="PDFs Generated" />
            <ProgressBar value={usage.apiCallsThisMonth} max={usage.apiCallsLimit} label="API Calls" />
            <ProgressBar value={usage.storageUsedMb} max={usage.storageLimitMb} label="Storage (MB)" />
            <div className="v2-stat-row">
                <div className="v2-stat"><span className="v2-stat-val">{usage.bulkJobsRun}</span><span className="v2-stat-lbl">Bulk Jobs</span></div>
            </div>
        </div>
    );
};

// ─── M4  OpenAPI + Docs ──────────────────────────────────────────
const M4Panel = ({ endpoints }) => {
    const [filter, setFilter] = useState('');
    const filtered = filter
        ? endpoints.filter((e) => e.path.includes(filter) || e.summary.toLowerCase().includes(filter.toLowerCase()))
        : endpoints;
    return (
        <div className="v2-panel">
            <SectionTitle>API Reference</SectionTitle>
            <input
                className="v2-input"
                placeholder="Filter endpoints..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
            />
            <table className="v2-table">
                <thead><tr><th>Method</th><th>Path</th><th>Summary</th><th>Auth</th><th>Scope</th><th>Status</th></tr></thead>
                <tbody>
                    {filtered.map((ep, i) => (
                        <tr key={i}>
                            <td><MethodBadge method={ep.method} /></td>
                            <td className="v2-mono">{ep.path}</td>
                            <td>{ep.summary}</td>
                            <td>{ep.auth}</td>
                            <td><code>{ep.scope}</code></td>
                            <td><Badge variant={ep.status === 'stable' ? 'stable' : 'beta'}>{ep.status}</Badge></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className="v2-hint">{filtered.length} endpoint(s) shown. Full OpenAPI spec will be available at <code>/api/v2/docs</code></p>
        </div>
    );
};

// ─── M5  Versioning + Library + Search ──────────────────────────────
//
// Day 10: Enriched with Restore + Compare buttons per version row, an
// inline VersionDiffView modal that renders element-level diffs with
// color coding (🟢 added / 🟡 modified / 🔴 removed), and a RestoreConfirm
// modal that asks the user to confirm before rolling back.
//
// All M5 endpoints route through v2ApiService which already wires to
// the real backend (mock fallback in try/catch). After Shadow Write
// deploy, the versions list reflects every Designer save automatically.
const M5Panel = ({ versions, searchResults, onVersionsRefresh }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(searchResults);

    // ── Restore + Compare modals state ────────────────────────────
    const [restoreTarget, setRestoreTarget] = useState(null);   // {templateId, name, version}
    const [diffTarget, setDiffTarget] = useState(null);          // {templateId, name, fromVersion, toVersion}

    const doSearch = async () => {
        const r = await api.searchLibrary(query);
        setResults(r);
    };

    const handleRestoreClick = (templateId, name, version) =>
        setRestoreTarget({ templateId, name, version });

    const handleCompareClick = (templateId, name, version, latestVersion) =>
        setDiffTarget({ templateId, name, fromVersion: version, toVersion: latestVersion });

    const closeRestore = () => setRestoreTarget(null);
    const closeDiff = () => setDiffTarget(null);

    const onRestoreSuccess = () => {
        closeRestore();
        // Reload version list so the newly created (post-restore) snapshot
        // shows up immediately. The dashboard's top-level loader exposes
        // onVersionsRefresh so panels can trigger a partial reload.
        if (onVersionsRefresh) onVersionsRefresh();
    };

    return (
        <div className="v2-panel">
            <SectionTitle>Template Versioning</SectionTitle>

            {versions.length === 0 ? (
                <div className="v2-empty-state">
                    <div className="v2-empty-icon">📜</div>
                    <div className="v2-empty-title">No versioned templates yet</div>
                    <div className="v2-empty-hint">
                        Your first Designer save creates v1. Each subsequent save adds a new version.
                    </div>
                </div>
            ) : (
                versions.map((tv) => {
                    const latestVersion = tv.versions[0]?.version;
                    return (
                        <div key={tv.templateId} className="v2-card">
                            <div className="v2-card-header">
                                {tv.name} <Badge variant="version">v{latestVersion}</Badge>
                            </div>
                            <table className="v2-table-sm">
                                <thead>
                                    <tr>
                                        <th>Ver</th>
                                        <th>Date</th>
                                        <th>Author</th>
                                        <th>Note</th>
                                        <th style={{ width: 180, textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tv.versions.map((v) => {
                                        const isLatest = v.version === latestVersion;
                                        return (
                                            <tr key={v.version}>
                                                <td>
                                                    v{v.version}
                                                    {isLatest && <span className="v2-current-marker"> ●</span>}
                                                </td>
                                                <td>{new Date(v.createdAt).toLocaleDateString()}</td>
                                                <td>{v.author}</td>
                                                <td>{v.changeNote}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {!isLatest && (
                                                        <>
                                                            <button
                                                                className="v2-btn-sm"
                                                                title="Compare this version with the latest"
                                                                onClick={() => handleCompareClick(
                                                                    tv.templateId, tv.name, v.version, latestVersion
                                                                )}
                                                            >
                                                                🔍 Compare
                                                            </button>
                                                            <button
                                                                className="v2-btn-sm v2-btn-warn"
                                                                title="Roll the template back to this version"
                                                                onClick={() => handleRestoreClick(
                                                                    tv.templateId, tv.name, v.version
                                                                )}
                                                            >
                                                                🔄 Restore
                                                            </button>
                                                        </>
                                                    )}
                                                    {isLatest && (
                                                        <span className="v2-muted v2-tiny">current</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    );
                })
            )}

            <SectionTitle>Template Library Search</SectionTitle>
            <div className="v2-search-row">
                <input
                    className="v2-input"
                    placeholder="Search templates..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                />
                <button className="v2-btn" onClick={doSearch}>Search</button>
            </div>
            <div className="v2-grid-3">
                {results.map((t) => (
                    <div key={t.id} className="v2-card v2-card-sm">
                        <strong>{t.name}</strong>
                        <div className="v2-muted">{t.category} &middot; v{t.version}</div>
                        <div className="v2-tags">
                            {t.tags.map((tag) => <span key={tag} className="v2-tag">#{tag}</span>)}
                        </div>
                    </div>
                ))}
            </div>

            {restoreTarget && (
                <RestoreConfirm
                    target={restoreTarget}
                    onClose={closeRestore}
                    onSuccess={onRestoreSuccess}
                />
            )}
            {diffTarget && (
                <VersionDiffView
                    target={diffTarget}
                    onClose={closeDiff}
                />
            )}
        </div>
    );
};

// ─── M5  RestoreConfirm modal ───────────────────────────────────────
//
// Two-step interaction: explain what restore does (new snapshot on top
// of history, original v1..vN preserved) and optionally collect a note
// for the audit trail. Submitting calls v2ApiService.restoreTemplateVersion
// which the backend turns into a fresh M5 snapshot.
const RestoreConfirm = ({ target, onClose, onSuccess }) => {
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const doRestore = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await api.restoreTemplateVersion(
                target.templateId,
                target.version,
                note.trim() || `Restored from v${target.version}`
            );
            onSuccess();
        } catch (err) {
            setError(err?.message || 'Restore failed');
            setSubmitting(false);
        }
    };

    return (
        <div className="v2-overlay" onClick={onClose}>
            <div className="v2-modal v2-modal-sm" onClick={(e) => e.stopPropagation()}>
                <div className="v2-modal-header">
                    <span>Restore <strong>{target.name}</strong> to v{target.version}?</span>
                    <button className="v2-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="v2-modal-body">
                    <p className="v2-muted">
                        This creates a new version on top of the existing history. Your current
                        content stays in place as the previous version — restore is reversible.
                    </p>
                    <label className="v2-form-label">Change note (optional):</label>
                    <input
                        className="v2-input"
                        placeholder={`Restored from v${target.version}`}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        autoFocus
                    />
                    {error && <div className="v2-error">{error}</div>}
                </div>
                <div className="v2-modal-footer">
                    <button className="v2-btn-ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                    <button className="v2-btn v2-btn-warn" onClick={doRestore} disabled={submitting}>
                        {submitting ? 'Restoring...' : `🔄 Restore to v${target.version}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── M5  VersionDiffView modal ──────────────────────────────────────
//
// Element-level diff renderer with TWO presentation modes:
//
//   1. VISUAL (default) — renders each version as an SVG overlay of an
//      A4 wireframe, with diff highlights:
//        🟢 added   = green glow + dashed border on the new element
//        🟡 modified = amber border around the element, with property
//                      change indicator
//        🔴 removed = red dashed outline (ghost) at the position the
//                     element used to occupy
//      Multi-page templates get a per-page tab strip. Side-by-side or
//      overlay mode toggle in the header.
//
//   2. PROPERTY — the original developer view: addedCount /
//      modifiedCount / removedCount summary pills + per-entry list with
//      dotted-path property diffs. Useful for power users and audit logs.
//
// The component fetches both the diff endpoint (for the structural
// change list) AND the two version snapshots (for the SVG rendering).
// All three loads happen in parallel; the modal shows a loading state
// until all three resolve.
const VersionDiffView = ({ target, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [diff, setDiff] = useState(null);
    const [fromVersion, setFromVersion] = useState(null);
    const [toVersion, setToVersion] = useState(null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('visual');        // 'visual' | 'property'
    const [viewMode, setViewMode] = useState('side-by-side');    // 'side-by-side' | 'overlay'
    const [activePage, setActivePage] = useState(1);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [d, vFrom, vTo] = await Promise.all([
                    api.getTemplateVersionDiff(target.templateId, target.fromVersion, target.toVersion),
                    api.getTemplateVersion(target.templateId, target.fromVersion),
                    api.getTemplateVersion(target.templateId, target.toVersion),
                ]);
                if (!cancelled) {
                    setDiff(d);
                    setFromVersion(vFrom);
                    setToVersion(vTo);
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Failed to load diff');
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [target.templateId, target.fromVersion, target.toVersion]);

    const entries = diff?.entries ?? [];
    const grouped = {
        added: entries.filter((e) => e.changeType === 'added'),
        modified: entries.filter((e) => e.changeType === 'modified'),
        removed: entries.filter((e) => e.changeType === 'removed'),
    };

    // Map of elementId → changeType for fast SVG lookup. Used by the
    // VisualDiffRenderer to decorate each element with its diff state.
    const changeMap = {};
    entries.forEach((e) => { changeMap[e.elementId] = e.changeType; });

    // Collect all pageIds present in either version (so a page that
    // existed in 'from' but was removed in 'to' still appears in the
    // tab strip).
    const pageIds = (() => {
        const set = new Set();
        const collect = (vd) => {
            try {
                const dj = vd?.designerJson ? JSON.parse(vd.designerJson) : null;
                if (dj?.pages) dj.pages.forEach((p) => set.add(p.id));
                if (dj?.objects) {
                    Object.values(dj.objects).forEach((o) => {
                        if (o?.PageId) set.add(o.PageId);
                    });
                }
            } catch (e) { /* parse error - ignore */ }
        };
        collect(fromVersion);
        collect(toVersion);
        return [...set].sort((a, b) => a - b);
    })();

    return (
        <div className="v2-overlay" onClick={onClose}>
            <div className="v2-modal v2-modal-xl" onClick={(e) => e.stopPropagation()}>
                <div className="v2-modal-header">
                    <span>
                        Compare <strong>{target.name}</strong>:
                        v{target.fromVersion} → v{target.toVersion}
                    </span>
                    <div className="v2-modal-header-actions">
                        <div className="v2-tab-row v2-tab-row-sm">
                            <button
                                className={`v2-tab-btn ${activeTab === 'visual' ? 'active' : ''}`}
                                onClick={() => setActiveTab('visual')}
                            >
                                🎨 Visual
                            </button>
                            <button
                                className={`v2-tab-btn ${activeTab === 'property' ? 'active' : ''}`}
                                onClick={() => setActiveTab('property')}
                            >
                                📋 Property
                            </button>
                        </div>
                        <button className="v2-modal-close" onClick={onClose}>×</button>
                    </div>
                </div>
                <div className="v2-modal-body">
                    {loading && <div className="v2-loading">Loading diff…</div>}
                    {error && <div className="v2-error">{error}</div>}

                    {!loading && !error && diff && (
                        <>
                            <div className="v2-diff-summary">
                                <span className="v2-diff-pill v2-diff-added">
                                    🟢 {diff.addedCount ?? grouped.added.length} added
                                </span>
                                <span className="v2-diff-pill v2-diff-modified">
                                    🟡 {diff.modifiedCount ?? grouped.modified.length} modified
                                </span>
                                <span className="v2-diff-pill v2-diff-removed">
                                    🔴 {diff.removedCount ?? grouped.removed.length} removed
                                </span>
                            </div>

                            {activeTab === 'visual' && (
                                <VisualDiffRenderer
                                    fromVersion={fromVersion}
                                    toVersion={toVersion}
                                    changeMap={changeMap}
                                    entries={entries}
                                    pageIds={pageIds}
                                    activePage={activePage}
                                    setActivePage={setActivePage}
                                    viewMode={viewMode}
                                    setViewMode={setViewMode}
                                />
                            )}

                            {activeTab === 'property' && (
                                <>
                                    {entries.length === 0 && (
                                        <div className="v2-empty-state v2-empty-inline">
                                            <div className="v2-empty-icon">✓</div>
                                            <div className="v2-empty-title">No changes between these versions</div>
                                            <div className="v2-empty-hint">
                                                These two snapshots are identical at the element level.
                                            </div>
                                        </div>
                                    )}
                                    {grouped.added.length > 0 && (
                                        <DiffSection label="Added elements" rows={grouped.added} type="added" />
                                    )}
                                    {grouped.modified.length > 0 && (
                                        <DiffSection label="Modified elements" rows={grouped.modified} type="modified" />
                                    )}
                                    {grouped.removed.length > 0 && (
                                        <DiffSection label="Removed elements" rows={grouped.removed} type="removed" />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
                <div className="v2-modal-footer">
                    <button className="v2-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

// ─── M5  VisualDiffRenderer — SVG-based template preview with diff overlay ──
//
// Parses the from/to designerJson snapshots, renders each version as
// an SVG inside an A4-proportioned viewbox, and decorates each element
// with its diff state (added/modified/removed). Multi-page templates
// expose a per-page tab strip; side-by-side mode shows both versions
// next to each other, overlay mode stacks them with the older version
// at reduced opacity.
//
// Designer's coordinate system is PDF points (595.28 × 841.19 for A4)
// stored as element.properties.location.{x,y} and element.properties
// .size.{width,height}. SVG viewBox uses the same coordinates so no
// conversion is needed — the SVG element scales the viewBox to the
// container width via preserveAspectRatio.
const VisualDiffRenderer = ({
    fromVersion, toVersion, changeMap, entries,
    pageIds, activePage, setActivePage, viewMode, setViewMode
}) => {
    // Parse both snapshots once and cache. Errors degrade gracefully —
    // the panel renders an inline error rather than blowing up the modal.
    let fromDesigner = null;
    let toDesigner = null;
    let parseError = null;
    try {
        fromDesigner = fromVersion?.designerJson ? JSON.parse(fromVersion.designerJson) : null;
        toDesigner = toVersion?.designerJson ? JSON.parse(toVersion.designerJson) : null;
    } catch (e) {
        parseError = e?.message || 'Failed to parse designer JSON';
    }

    if (parseError) {
        return (
            <div className="v2-error">
                Visual diff unavailable — designer JSON parse error: {parseError}
            </div>
        );
    }

    if (!fromDesigner && !toDesigner) {
        return (
            <div className="v2-empty-state v2-empty-inline">
                <div className="v2-empty-icon">🗎</div>
                <div className="v2-empty-title">No designer content to visualize</div>
                <div className="v2-empty-hint">
                    Both versions are empty or could not be loaded.
                </div>
            </div>
        );
    }

    return (
        <div className="v2-visual-diff">
            {/* Toolbar: page navigator + side-by-side / overlay toggle */}
            <div className="v2-visual-toolbar">
                {pageIds.length > 1 && (
                    <div className="v2-tab-row v2-tab-row-sm">
                        {pageIds.map((pid) => (
                            <button
                                key={pid}
                                className={`v2-tab-btn ${activePage === pid ? 'active' : ''}`}
                                onClick={() => setActivePage(pid)}
                            >
                                Page {pid}
                            </button>
                        ))}
                    </div>
                )}
                <div className="v2-visual-toolbar-spacer" />
                <div className="v2-tab-row v2-tab-row-sm">
                    <button
                        className={`v2-tab-btn ${viewMode === 'side-by-side' ? 'active' : ''}`}
                        onClick={() => setViewMode('side-by-side')}
                    >
                        ⫼ Side-by-side
                    </button>
                    <button
                        className={`v2-tab-btn ${viewMode === 'overlay' ? 'active' : ''}`}
                        onClick={() => setViewMode('overlay')}
                    >
                        ⊞ Overlay
                    </button>
                </div>
            </div>

            {/* Color legend */}
            <div className="v2-visual-legend">
                <span className="v2-visual-legend-item">
                    <span className="v2-visual-legend-swatch v2-diff-added" />
                    Added
                </span>
                <span className="v2-visual-legend-item">
                    <span className="v2-visual-legend-swatch v2-diff-modified" />
                    Modified
                </span>
                <span className="v2-visual-legend-item">
                    <span className="v2-visual-legend-swatch v2-diff-removed" />
                    Removed
                </span>
            </div>

            {/* Canvas: side-by-side or overlay */}
            {viewMode === 'side-by-side' && (
                <div className="v2-visual-canvas-row">
                    <div className="v2-visual-canvas-col">
                        <div className="v2-visual-canvas-label">
                            v{fromVersion?.version} (from)
                        </div>
                        <PageSvg
                            designer={fromDesigner}
                            pageId={activePage}
                            changeMap={changeMap}
                            renderMode="from"
                        />
                    </div>
                    <div className="v2-visual-canvas-col">
                        <div className="v2-visual-canvas-label">
                            v{toVersion?.version} (to)
                        </div>
                        <PageSvg
                            designer={toDesigner}
                            pageId={activePage}
                            changeMap={changeMap}
                            renderMode="to"
                        />
                    </div>
                </div>
            )}

            {viewMode === 'overlay' && (
                <div className="v2-visual-canvas-row">
                    <div className="v2-visual-canvas-col v2-visual-canvas-full">
                        <div className="v2-visual-canvas-label">
                            v{fromVersion?.version} → v{toVersion?.version} (overlay)
                        </div>
                        <PageSvg
                            designer={toDesigner}
                            fromDesigner={fromDesigner}
                            pageId={activePage}
                            changeMap={changeMap}
                            renderMode="overlay"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── PageSvg — renders one page from a designerJson snapshot ────────
//
// In "from" mode, elements removed in the new version stay colored red
// (ghost outline). In "to" mode, elements added in the new version are
// highlighted green and modified ones amber. Overlay mode shows the new
// version with the old version's removed elements ghosted at reduced
// opacity behind.
const PageSvg = ({ designer, fromDesigner, pageId, changeMap, renderMode }) => {
    if (!designer) {
        return <div className="v2-visual-canvas-empty">Empty</div>;
    }

    // Locate the page record (or fall back to A4 if missing).
    const page = designer.pages?.find((p) => p.id === pageId)
        ?? { width: 595.28, height: 841.19 };

    // Filter elements for this page and sort by Zindex so the SVG
    // paint order matches the Designer's rendering order.
    const elements = Object.values(designer.objects || {})
        .filter((el) => (el.PageId ?? 1) === pageId)
        .sort((a, b) => (a.properties?.Zindex ?? 0) - (b.properties?.Zindex ?? 0));

    // For overlay mode, also pick up removed elements from fromDesigner.
    let ghostElements = [];
    if (renderMode === 'overlay' && fromDesigner) {
        ghostElements = Object.values(fromDesigner.objects || {})
            .filter((el) => (el.PageId ?? 1) === pageId)
            .filter((el) => changeMap[el.id] === 'removed')
            .sort((a, b) => (a.properties?.Zindex ?? 0) - (b.properties?.Zindex ?? 0));
    }

    return (
        <div className="v2-visual-page-wrap">
            <svg
                className="v2-visual-page-svg"
                viewBox={`0 0 ${page.width} ${page.height}`}
                preserveAspectRatio="xMinYMin meet"
                xmlns="http://www.w3.org/2000/svg"
            >
                {/* Page background */}
                <rect
                    x="0" y="0"
                    width={page.width}
                    height={page.height}
                    fill="white"
                    stroke="#e5e7eb"
                    strokeWidth="0.5"
                />

                {/* Ghost layer (overlay mode only) — shows removed-in-to elements */}
                {ghostElements.map((el) => (
                    <ElementSvg
                        key={`ghost-${el.id}`}
                        element={el}
                        changeType="removed"
                        ghost={true}
                    />
                ))}

                {/* Main elements */}
                {elements.map((el) => {
                    const changeType = changeMap[el.id];
                    // In "from" view, items removed in the new version are
                    // highlighted; in "to" view, added items are highlighted.
                    // Both views highlight modified items.
                    const shouldHighlight =
                        (renderMode === 'from' && changeType === 'removed') ||
                        (renderMode === 'to' && (changeType === 'added' || changeType === 'modified')) ||
                        (renderMode === 'overlay' && (changeType === 'added' || changeType === 'modified'));

                    return (
                        <ElementSvg
                            key={el.id}
                            element={el}
                            changeType={shouldHighlight ? changeType : null}
                        />
                    );
                })}
            </svg>
        </div>
    );
};

// ─── ElementSvg — type dispatcher + diff overlay ────────────────────
//
// One <g> per element. The element-specific renderer fills in the
// content; a final overlay <rect> on top of the group draws the diff
// highlight (when applicable). Coordinate origin is the page top-left
// in PDF points — same as the Designer's stored values, so no
// transform math is needed.
const ElementSvg = ({ element, changeType, ghost }) => {
    if (!element?.properties) return null;
    const { location, size } = element.properties;
    if (!location || !size) return null;

    const x = location.x ?? 0;
    const y = location.y ?? 0;
    const w = size.width ?? 0;
    const h = size.height ?? 0;

    const opacity = ghost ? 0.35 : 1;

    return (
        <g
            data-element-id={element.id}
            data-change-type={changeType || ''}
            opacity={opacity}
        >
            {renderElementByType(element, x, y, w, h)}

            {changeType && (
                <rect
                    x={x - 1}
                    y={y - 1}
                    width={w + 2}
                    height={h + 2}
                    className={`v2-svg-diff-overlay v2-svg-diff-${changeType}`}
                    pointerEvents="none"
                />
            )}
        </g>
    );
};

// ─── renderElementByType — type-specific SVG fragments ──────────────
//
// 10 supported types. The renderer for each type only depends on the
// element's own properties — no Redux, no global state. Types that
// can't be visually rendered (Word's rich text, Signature's certificate
// chain) fall back to a labeled placeholder rather than failing.
const renderElementByType = (element, x, y, w, h) => {
    const props = element.properties || {};
    const type = element.type;

    switch (type) {
        case 'TextBox':
            return renderTextBox(element, x, y, w, h);
        case 'Word':
            return renderWord(element, x, y, w, h);
        case 'Picture':
            return renderPicture(element, x, y, w, h);
        case 'Signature':
            return renderSignature(element, x, y, w, h);
        case 'Barcode':
            return renderBarcode(element, x, y, w, h);
        case 'Rectangle':
            return renderRectangle(element, x, y, w, h);
        case 'Ellipse':
            return renderEllipse(element, x, y, w, h);
        case 'Lines':
            return renderLines(element, x, y, w, h);
        case 'Table':
            return renderTable(element, x, y, w, h);
        case 'Slider':
            return renderPlaceholder(element, x, y, w, h, 'Slider', '⇿');
        default:
            return renderPlaceholder(element, x, y, w, h, type || 'Unknown', '?');
    }
};

const safeColor = (c, fallback = '#000000') => {
    if (!c || c === 'transparent') return fallback;
    if (c === 'black') return '#000000';   // V1 legacy literal
    if (c === 'white') return '#ffffff';
    return c;
};

const renderTextBox = (element, x, y, w, h) => {
    const p = element.properties || {};
    const text = p.Text ?? element.content ?? '';
    const fontSize = parseFloat(p.FontSize) || 12;
    const font = p.Font || 'Arial';
    const fill = safeColor(p.colors?.color);
    const bg = p.colors?.backgroundColor;
    const align = p.Align;   // "0" left, "1" right, "2" center, "3" justify
    const textAnchor = align === '2' ? 'middle' : align === '1' ? 'end' : 'start';
    const textX = align === '2' ? x + w / 2 : align === '1' ? x + w : x + 2;
    const fontWeight = p.Bold ? 'bold' : 'normal';
    const fontStyle = p.Italic ? 'italic' : 'normal';

    return (
        <>
            {bg && bg !== 'transparent' && (
                <rect x={x} y={y} width={w} height={h} fill={safeColor(bg, 'transparent')} />
            )}
            <text
                x={textX}
                y={y + fontSize + 1}
                fontFamily={font}
                fontSize={fontSize}
                fontWeight={fontWeight}
                fontStyle={fontStyle}
                fill={fill}
                textAnchor={textAnchor}
            >
                {String(text).slice(0, 200)}
            </text>
        </>
    );
};

const renderWord = (element, x, y, w, h) => {
    const p = element.properties || {};
    // Try to extract plain text from Draft.js ContentState. If empty,
    // fall back to "Word" placeholder. We don't render rich-text inline
    // styling — that's a future enhancement.
    let plain = '';
    try {
        const cs = p.ContentState;
        if (cs && typeof cs === 'object' && Array.isArray(cs.blocks)) {
            plain = cs.blocks.map((b) => b.text || '').join(' ').trim();
        } else if (typeof cs === 'string' && cs.length > 0) {
            plain = cs;
        }
    } catch (e) { /* ignore */ }

    if (!plain) {
        return renderPlaceholder(element, x, y, w, h, 'Word', '📝');
    }

    return (
        <>
            <rect x={x} y={y} width={w} height={h} fill="#fafbfc" stroke="#e5e7eb" strokeWidth="0.5" />
            <text
                x={x + 4}
                y={y + 14}
                fontFamily="Arial"
                fontSize="11"
                fill={safeColor(p.colors?.color)}
            >
                {plain.slice(0, 150)}
            </text>
        </>
    );
};

const renderPicture = (element, x, y, w, h) => {
    const p = element.properties || {};
    const blob = p.JpgBlob;

    // Base64 blob may be a data URI ("data:image/png;base64,...") or a
    // bare base64 string. The <image> href accepts both forms when the
    // data URI prefix is included; bare base64 we wrap explicitly.
    let href = null;
    if (blob && typeof blob === 'string' && blob.length > 100) {
        href = blob.startsWith('data:') ? blob : `data:image/png;base64,${blob}`;
    }

    if (!href) {
        return (
            <>
                <rect x={x} y={y} width={w} height={h} fill="#f3f4f6" stroke="#d1d5db" strokeDasharray="4 2" />
                <text
                    x={x + w / 2}
                    y={y + h / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="10"
                    fill="#6b7280"
                >
                    🖼 Picture
                </text>
            </>
        );
    }

    return (
        <image
            x={x}
            y={y}
            width={w}
            height={h}
            href={href}
            preserveAspectRatio="xMidYMid meet"
        />
    );
};

const renderSignature = (element, x, y, w, h) => {
    const p = element.properties || {};
    // Signatures sometimes have a JpgBlob (visual preview), often not.
    if (p.JpgBlob && typeof p.JpgBlob === 'string' && p.JpgBlob.length > 100) {
        return renderPicture(element, x, y, w, h);
    }
    return (
        <>
            <rect x={x} y={y} width={w} height={h}
                fill="#fffbeb" stroke="#fbbf24" strokeDasharray="3 2" strokeWidth="0.5" />
            <text
                x={x + w / 2}
                y={y + h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10"
                fill="#92400e"
            >
                ✍ Signature
            </text>
        </>
    );
};

const renderBarcode = (element, x, y, w, h) => {
    const p = element.properties || {};
    const type = p.BarcodeType || 'EAN128';
    const fill = safeColor(p.colors?.color);
    const isQR = type === 'QR';

    if (isQR) {
        // QR placeholder: nested squares to suggest a QR module pattern.
        const size = Math.min(w, h);
        const cx = x + (w - size) / 2;
        const cy = y + (h - size) / 2;
        return (
            <>
                <rect x={cx} y={cy} width={size} height={size} fill="white" stroke={fill} strokeWidth="0.5" />
                <rect x={cx + size * 0.1} y={cy + size * 0.1} width={size * 0.2} height={size * 0.2} fill={fill} />
                <rect x={cx + size * 0.7} y={cy + size * 0.1} width={size * 0.2} height={size * 0.2} fill={fill} />
                <rect x={cx + size * 0.1} y={cy + size * 0.7} width={size * 0.2} height={size * 0.2} fill={fill} />
                <rect x={cx + size * 0.4} y={cy + size * 0.4} width={size * 0.2} height={size * 0.2} fill={fill} />
            </>
        );
    }

    // EAN128 / I25: linear barcode — render vertical stripes.
    const stripeCount = 24;
    const stripeWidth = w / stripeCount;
    const stripes = [];
    for (let i = 0; i < stripeCount; i++) {
        // Pseudo-random but deterministic pattern from the index.
        if ((i * 7) % 3 !== 0) {
            stripes.push(
                <rect
                    key={i}
                    x={x + i * stripeWidth}
                    y={y}
                    width={stripeWidth * 0.6}
                    height={h * 0.85}
                    fill={fill}
                />
            );
        }
    }
    return (
        <>
            <rect x={x} y={y} width={w} height={h} fill="white" stroke={fill} strokeWidth="0.3" />
            {stripes}
            <text
                x={x + w / 2}
                y={y + h - 1}
                textAnchor="middle"
                fontSize="6"
                fill={fill}
            >
                {type}
            </text>
        </>
    );
};

const renderRectangle = (element, x, y, w, h) => {
    const p = element.properties || {};
    const stroke = safeColor(p.BorderColor);
    const strokeWidth = parseFloat(p.BorderWidth) || 1;
    const dashArray = (p.BorderStyle === 'Dashed') ? '4 2'
        : (p.BorderStyle === 'Dotted') ? '1 2'
        : null;
    const fillColor = p.Fill ? safeColor(p.FillColor, 'transparent') : 'transparent';

    return (
        <rect
            x={x}
            y={y}
            width={w}
            height={h}
            fill={fillColor}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray || undefined}
        />
    );
};

const renderEllipse = (element, x, y, w, h) => {
    const p = element.properties || {};
    const stroke = safeColor(p.BorderColor);
    const strokeWidth = parseFloat(p.BorderWidth) || 1;
    const dashArray = (p.BorderStyle === 'Dashed') ? '4 2'
        : (p.BorderStyle === 'Dotted') ? '1 2'
        : null;
    const fillColor = p.Fill ? safeColor(p.FillColor, 'transparent') : 'transparent';

    return (
        <ellipse
            cx={x + w / 2}
            cy={y + h / 2}
            rx={w / 2}
            ry={h / 2}
            fill={fillColor}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray || undefined}
        />
    );
};

const renderLines = (element, x, y, w, h) => {
    const p = element.properties || {};
    const stroke = safeColor(p.BorderColor);
    const strokeWidth = parseFloat(p.BorderWidth) || 1;
    const dashArray = (p.BorderStyle === 'Dashed') ? '4 2'
        : (p.BorderStyle === 'Dotted') ? '1 2'
        : null;
    const dir = p.Direction || 'Free';

    let x1, y1, x2, y2;
    if (dir === 'Horizontal') {
        x1 = x; y1 = y + h / 2; x2 = x + w; y2 = y + h / 2;
    } else if (dir === 'Vertical') {
        x1 = x + w / 2; y1 = y; x2 = x + w / 2; y2 = y + h;
    } else if (dir === 'Flip') {
        x1 = x + w; y1 = y; x2 = x; y2 = y + h;
    } else {
        // 'Free' or default — diagonal top-left → bottom-right
        x1 = x; y1 = y; x2 = x + w; y2 = y + h;
    }

    return (
        <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray || undefined}
        />
    );
};

const renderTable = (element, x, y, w, h) => {
    const p = element.properties || {};
    const data = Array.isArray(p.TableData) ? p.TableData : [];
    const borderColor = safeColor(p.BorderColor);
    const borderWidth = parseFloat(p.BorderWidth) || 1;
    const hasHeader = p.HasHeader !== false;
    const zebra = p.ZebraStripe;
    const alt = safeColor(p.AlternateColor, '#f5f5f5');
    const headerBg = safeColor(p.HeaderBgColor, '#cccccc');
    const headerFg = safeColor(p.HeaderFontColor, '#000000');
    const headerH = parseFloat(p.HeaderHeight) || 20;
    const rowH = parseFloat(p.RowHeight) || 16;

    if (data.length === 0) {
        return (
            <>
                <rect x={x} y={y} width={w} height={h}
                    fill="#fafbfc" stroke={borderColor} strokeWidth={borderWidth} />
                <text
                    x={x + w / 2}
                    y={y + h / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="10"
                    fill="#9ca3af"
                >
                    ⊞ Table (empty)
                </text>
            </>
        );
    }

    // Render each row as a horizontal band; cells as rects with text.
    let curY = y;
    const rows = [];
    data.forEach((row, rIdx) => {
        const isHeader = rIdx === 0 && hasHeader;
        const rh = isHeader ? headerH : rowH;
        if (curY + rh > y + h) return;   // clip

        const cells = Array.isArray(row.Cells) ? row.Cells : [];
        const totalSpan = cells.reduce((s, c) => s + (c.span || 1), 0) || 1;
        const cellW = w / totalSpan;
        let curX = x;
        cells.forEach((cell, cIdx) => {
            const span = cell.span || 1;
            const cw = cellW * span;
            const cbg = isHeader
                ? headerBg
                : (zebra && rIdx % 2 === 1 ? alt : safeColor(cell.bg, 'transparent'));
            const cfg = isHeader ? headerFg : safeColor(cell.c, '#000000');
            rows.push(
                <g key={`${rIdx}-${cIdx}`}>
                    {cbg !== 'transparent' && (
                        <rect x={curX} y={curY} width={cw} height={rh} fill={cbg} />
                    )}
                    <rect
                        x={curX}
                        y={curY}
                        width={cw}
                        height={rh}
                        fill="transparent"
                        stroke={borderColor}
                        strokeWidth={borderWidth * 0.5}
                    />
                    <text
                        x={curX + 2}
                        y={curY + rh - 3}
                        fontSize={isHeader ? 9 : 8}
                        fontWeight={isHeader ? 'bold' : 'normal'}
                        fill={cfg}
                    >
                        {String(cell.v ?? '').slice(0, 30)}
                    </text>
                </g>
            );
            curX += cw;
        });
        curY += rh;
    });

    return (
        <>
            <rect x={x} y={y} width={w} height={h}
                fill="transparent" stroke={borderColor} strokeWidth={borderWidth} />
            {rows}
        </>
    );
};

const renderPlaceholder = (element, x, y, w, h, label, icon) => (
    <>
        <rect
            x={x} y={y} width={w} height={h}
            fill="#f3f4f6"
            stroke="#9ca3af"
            strokeDasharray="3 2"
            strokeWidth="0.5"
        />
        <text
            x={x + w / 2}
            y={y + h / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="10"
            fill="#6b7280"
        >
            {icon} {label}
        </text>
    </>
);

const DiffSection = ({ label, rows, type }) => (
    <div className="v2-diff-section">
        <div className={`v2-diff-section-title v2-diff-${type}`}>{label} ({rows.length})</div>
        {rows.map((r, i) => (
            <div key={`${r.elementId}-${i}`} className={`v2-diff-row v2-diff-${type}`}>
                <div className="v2-diff-element-id">
                    <code>{r.elementId}</code>
                    {r.elementType && <span className="v2-muted v2-tiny"> · {r.elementType}</span>}
                </div>
                {type === 'modified' && r.propertyChanges?.length > 0 && (
                    <div className="v2-diff-properties">
                        {r.propertyChanges.map((p, j) => (
                            <div key={j} className="v2-diff-property">
                                <code>{p.path}</code>:
                                <span className="v2-diff-old"> {formatValue(p.oldValue)} </span>
                                →
                                <span className="v2-diff-new"> {formatValue(p.newValue)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ))}
    </div>
);

const formatValue = (v) => {
    if (v === null || v === undefined) return <em>(none)</em>;
    if (typeof v === 'string') return v.length > 40 ? `"${v.slice(0, 40)}…"` : `"${v}"`;
    if (typeof v === 'object') return JSON.stringify(v).slice(0, 60);
    return String(v);
};

// ─── M6  SDKs ────────────────────────────────────────────────────
const M6Panel = ({ sdkInfo }) => {
    const [activeTab, setActiveTab] = useState('javascript');
    if (!sdkInfo) return <div className="v2-loading">Loading...</div>;
    const sdk = sdkInfo[activeTab];
    return (
        <div className="v2-panel">
            <SectionTitle>SDK Quick Start</SectionTitle>
            <div className="v2-tab-row">
                <button className={`v2-tab-btn ${activeTab === 'javascript' ? 'active' : ''}`} onClick={() => setActiveTab('javascript')}>JavaScript / TypeScript</button>
                <button className={`v2-tab-btn ${activeTab === 'python' ? 'active' : ''}`} onClick={() => setActiveTab('python')}>Python</button>
            </div>
            <div className="v2-card">
                <div className="v2-card-row"><strong>Package:</strong> <code>{sdk.packageName}</code></div>
                <div className="v2-card-row"><strong>Version:</strong> {sdk.version}</div>
                <div className="v2-card-row"><strong>Install:</strong> <code className="v2-install-cmd">{sdk.installCmd}</code></div>
                <div className="v2-card-row"><strong>Repo:</strong> <a href={sdk.repoUrl} target="_blank" rel="noreferrer">{sdk.repoUrl}</a></div>
            </div>
            <SectionTitle>Code Example</SectionTitle>
            <pre className="v2-code-block"><code>{sdk.codeExample}</code></pre>
        </div>
    );
};

// ─── M7  Signed PDF + Cert Management ───────────────────────────
// ─── M7  Signed PDF + Cert Management ────────────────────────────
//
// Mirrors M5Panel's structure (in-panel modals + onRefresh callback)
// so the wiring conventions stay consistent across the dashboard.
// Three writes: upload (form), set-default (one click), revoke (modal).
const M7Panel = ({ certificates, signedPdfs, onCertsRefresh }) => {
    // ── Modal state ──────────────────────────────────────────────
    const [showUpload, setShowUpload] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null); // {id, name}

    // ── Inline action state (set-default has no modal) ───────────
    const [defaultingId, setDefaultingId] = useState(null);
    const [actionError, setActionError] = useState(null);

    const closeUpload = () => setShowUpload(false);
    const closeRevoke = () => setRevokeTarget(null);

    const onUploadSuccess = () => {
        closeUpload();
        if (onCertsRefresh) onCertsRefresh();
    };
    const onRevokeSuccess = () => {
        closeRevoke();
        if (onCertsRefresh) onCertsRefresh();
    };

    const handleSetDefault = async (cert) => {
        if (cert.isDefault || !cert.isActive) return;
        setDefaultingId(cert.id);
        setActionError(null);
        try {
            await api.setDefaultCertificate(cert.id);
            if (onCertsRefresh) await onCertsRefresh();
        } catch (err) {
            setActionError(err?.response?.data?.error || err?.message || 'Set-default failed');
        } finally {
            setDefaultingId(null);
        }
    };

    // ── Defensive field readers (real API vs legacy mock shape) ──
    // Backend (real):  notBefore / notAfter / signatureAlgorithm / isActive
    // Legacy mock:     validFrom / validTo / algorithm           / status
    const validFrom = (c) => c.notBefore || c.validFrom;
    const validTo   = (c) => c.notAfter  || c.validTo;
    const algorithm = (c) => c.signatureAlgorithm || c.algorithm;
    const isActive  = (c) =>
        typeof c.isActive === 'boolean' ? c.isActive : c.status === 'active';

    return (
        <div className="v2-panel">
            <div className="v2-section-header">
                <SectionTitle>Certificates</SectionTitle>
                <button
                    className="v2-btn"
                    onClick={() => { setActionError(null); setShowUpload(true); }}
                >
                    ⬆️ Upload PFX
                </button>
            </div>

            {actionError && <div className="v2-error">{actionError}</div>}

            {certificates.length === 0 ? (
                <div className="v2-empty-state">
                    <div className="v2-empty-icon">🔏</div>
                    <div className="v2-empty-title">No certificates yet</div>
                    <div className="v2-empty-hint">
                        Upload a PKCS#12 (.pfx / .p12) bundle to enable signed PDFs.
                        The PFX is encrypted at rest; the password never leaves the
                        backend after the initial validation.
                    </div>
                </div>
            ) : (
                <table className="v2-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Issuer</th>
                            <th>Valid</th>
                            <th>Algorithm</th>
                            <th>Status</th>
                            <th style={{ width: 220, textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {certificates.map((c) => {
                            const active = isActive(c);
                            return (
                                <tr key={c.id} className={active ? '' : 'v2-row-revoked'}>
                                    <td>
                                        {c.name}
                                        {c.isDefault && (
                                            <span
                                                className="v2-current-marker"
                                                title="Account default certificate"
                                            > 👑</span>
                                        )}
                                    </td>
                                    <td className="v2-cell-truncate" title={c.issuer}>{c.issuer}</td>
                                    <td>
                                        {validFrom(c) ? new Date(validFrom(c)).toLocaleDateString() : '—'}
                                        {' — '}
                                        {validTo(c) ? new Date(validTo(c)).toLocaleDateString() : '—'}
                                    </td>
                                    <td>{algorithm(c) || '—'}</td>
                                    <td>
                                        <Badge variant={active ? 'stable' : 'error'}>
                                            {active ? 'active' : 'revoked'}
                                        </Badge>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {active && !c.isDefault && (
                                            <button
                                                className="v2-btn-sm"
                                                title="Make this the account default certificate"
                                                disabled={defaultingId === c.id}
                                                onClick={() => handleSetDefault(c)}
                                            >
                                                {defaultingId === c.id ? '...' : '👑 Set Default'}
                                            </button>
                                        )}
                                        {active && (
                                            <button
                                                className="v2-btn-sm v2-btn-warn"
                                                title="Soft-revoke this certificate"
                                                onClick={() => setRevokeTarget({ id: c.id, name: c.name })}
                                            >
                                                🚫 Revoke
                                            </button>
                                        )}
                                        {!active && (
                                            <span className="v2-muted v2-tiny">revoked</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            <SectionTitle>Signed PDFs</SectionTitle>
            {signedPdfs.length === 0 ? (
                <div className="v2-empty-state v2-empty-inline">
                    <div className="v2-empty-hint">
                        No signed PDFs yet. Element-level signing in the Designer
                        produces signed PDFs that appear here once telemetry ships.
                    </div>
                </div>
            ) : (
                <table className="v2-table">
                    <thead><tr><th>Template</th><th>Certificate</th><th>Generated</th><th>Signature</th><th>Download</th></tr></thead>
                    <tbody>
                        {signedPdfs.map((p) => (
                            <tr key={p.id}>
                                <td>{p.templateName}</td>
                                <td>{p.certName}</td>
                                <td>{new Date(p.generatedAt).toLocaleString()}</td>
                                <td><Badge variant="stable">{p.signatureStatus}</Badge></td>
                                <td><a href={p.downloadUrl} className="v2-link">Download</a></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showUpload && (
                <CertificateUploadForm
                    onClose={closeUpload}
                    onSuccess={onUploadSuccess}
                />
            )}
            {revokeTarget && (
                <RevokeConfirm
                    target={revokeTarget}
                    onClose={closeRevoke}
                    onSuccess={onRevokeSuccess}
                />
            )}
        </div>
    );
};

// ─── M7  CertificateUploadForm modal ────────────────────────────────
//
// Three-step user flow:
//   1. Pick PFX file (file input, accept=".pfx,.p12")
//   2. Enter password (PFX passwords are required by the backend)
//   3. Optionally set a display name (defaults to subject DN)
//
// File is base64-encoded with FileReader. We strip the "data:...;base64,"
// prefix before sending — the backend expects bare base64.
//
// Errors are surfaced verbatim from the backend's RFC 7807 problem+json:
//   400 invalid_pfx → bad password / no private key / corrupt bundle
//   400 pfx_base64_required / password_required → caller bug
//   403 → API key lacks certificates.manage (won't hit via JWT)
const CertificateUploadForm = ({ onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        // Soft size cap — server reads PFX bytes into memory; 5 MB is
        // generous for a typical signing PFX (most are <50 KB).
        if (f.size > 5 * 1024 * 1024) {
            setError('PFX file too large (max 5 MB).');
            return;
        }
        setError(null);
        setFile(f);
    };

    const doUpload = async () => {
        if (!file) { setError('Please pick a .pfx or .p12 file.'); return; }
        if (!password) { setError('Password is required.'); return; }

        setSubmitting(true);
        setError(null);
        try {
            // FileReader → data URL → strip prefix to bare base64.
            const pfxBase64 = await new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => {
                    const dataUrl = r.result || '';
                    const idx = dataUrl.indexOf(',');
                    resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
                };
                r.onerror = () => reject(new Error('Could not read file'));
                r.readAsDataURL(file);
            });
            await api.uploadCertificate({
                pfxBase64,
                password,
                name: name || null,
            });
            onSuccess();
        } catch (err) {
            // Backend returns { error, detail? } on 400; surface both.
            const body = err?.response?.data;
            if (body?.error === 'invalid_pfx' && body.detail) {
                setError(`Invalid PFX: ${body.detail}`);
            } else if (body?.error) {
                setError(body.error);
            } else {
                setError(err?.message || 'Upload failed');
            }
            setSubmitting(false);
        }
    };

    return (
        <div className="v2-overlay" onClick={onClose}>
            <div className="v2-modal v2-modal-sm" onClick={(e) => e.stopPropagation()}>
                <div className="v2-modal-header">
                    <span>Upload signing certificate</span>
                    <button className="v2-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="v2-modal-body">
                    <p className="v2-muted">
                        Upload a PKCS#12 (.pfx / .p12) bundle. The password validates
                        the PFX at upload time; both the bytes and the password are
                        encrypted at rest and never returned by the API.
                    </p>

                    <label className="v2-form-label">PFX file:</label>
                    <div className="v2-file-row">
                        <input
                            type="file"
                            accept=".pfx,.p12"
                            onChange={onFileChange}
                            className="v2-file-input"
                        />
                        {file && (
                            <span className="v2-muted v2-tiny">
                                {file.name} &middot; {(file.size / 1024).toFixed(1)} KB
                            </span>
                        )}
                    </div>

                    <label className="v2-form-label">Password:</label>
                    <input
                        className="v2-input"
                        type="password"
                        placeholder="PFX password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="off"
                    />

                    <label className="v2-form-label">Display name (optional):</label>
                    <input
                        className="v2-input"
                        placeholder="Defaults to the certificate subject"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />

                    {error && <div className="v2-error">{error}</div>}
                </div>
                <div className="v2-modal-footer">
                    <button className="v2-btn-ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                    <button className="v2-btn" onClick={doUpload} disabled={submitting || !file || !password}>
                        {submitting ? 'Uploading...' : '⬆️ Upload'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── M7  RevokeConfirm modal ────────────────────────────────────────
//
// Soft-revoke is non-destructive (the cert document stays for audit)
// but it IS irreversible from the customer's perspective: once revoked
// they have to re-upload the PFX to bring it back. Two-step confirm
// matches the M5 RestoreConfirm pattern.
const RevokeConfirm = ({ target, onClose, onSuccess }) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const doRevoke = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await api.revokeCertificate(target.id);
            onSuccess();
        } catch (err) {
            const body = err?.response?.data;
            setError(body?.error || err?.message || 'Revoke failed');
            setSubmitting(false);
        }
    };

    return (
        <div className="v2-overlay" onClick={onClose}>
            <div className="v2-modal v2-modal-sm" onClick={(e) => e.stopPropagation()}>
                <div className="v2-modal-header">
                    <span>Revoke <strong>{target.name}</strong>?</span>
                    <button className="v2-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="v2-modal-body">
                    <p className="v2-muted">
                        Soft-revoke marks the certificate inactive. It can no longer
                        be used for signing and is cleared from the account default
                        if it held that role. The certificate document is kept for
                        audit. To use this certificate again you must re-upload it.
                    </p>
                    {error && <div className="v2-error">{error}</div>}
                </div>
                <div className="v2-modal-footer">
                    <button className="v2-btn-ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                    <button className="v2-btn v2-btn-warn" onClick={doRevoke} disabled={submitting}>
                        {submitting ? 'Revoking...' : '🚫 Revoke'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── M8  Audit Log ───────────────────────────────────────────────
const M8Panel = ({ auditLog }) => {
    const [severityFilter, setSeverityFilter] = useState('all');
    const filtered = severityFilter === 'all' ? auditLog : auditLog.filter((l) => l.severity === severityFilter);
    return (
        <div className="v2-panel">
            <SectionTitle>Audit Log</SectionTitle>
            <div className="v2-filter-row">
                <span>Filter:</span>
                {['all', 'info', 'warning', 'error'].map((s) => (
                    <button key={s} className={`v2-filter-btn ${severityFilter === s ? 'active' : ''}`} onClick={() => setSeverityFilter(s)}>{s}</button>
                ))}
            </div>
            <table className="v2-table">
                <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Resource</th><th>Detail</th><th>Severity</th></tr></thead>
                <tbody>
                    {filtered.map((l) => (
                        <tr key={l.id}>
                            <td className="v2-mono-sm">{new Date(l.timestamp).toLocaleString()}</td>
                            <td>{l.actor}</td>
                            <td><code>{l.action}</code></td>
                            <td className="v2-mono-sm">{l.resource}</td>
                            <td>{l.detail}</td>
                            <td><Badge variant={l.severity === 'warning' ? 'beta' : l.severity === 'error' ? 'error' : 'stable'}>{l.severity}</Badge></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── M8.5  Storage + Backup ──────────────────────────────────────
const M8_5Panel = ({ storage }) => {
    if (!storage) return <div className="v2-loading">Loading...</div>;
    return (
        <div className="v2-panel">
            <SectionTitle>Storage Overview</SectionTitle>
            <ProgressBar value={storage.totalUsedMb} max={storage.totalLimitMb} label="Total Storage" />
            <table className="v2-table-sm">
                <thead><tr><th>Type</th><th>Size (MB)</th><th>Count</th></tr></thead>
                <tbody>
                    {storage.breakdown.map((b, i) => (
                        <tr key={i}><td>{b.type}</td><td>{b.sizeMb}</td><td>{b.count}</td></tr>
                    ))}
                </tbody>
            </table>

            <SectionTitle>Configuration</SectionTitle>
            <div className="v2-card">
                <div className="v2-card-row"><strong>Provider:</strong> {storage.storageConfig.providerLabel}</div>
                <div className="v2-card-row"><strong>Base Path:</strong> <code>{storage.storageConfig.basePath}</code></div>
                <div className="v2-card-row"><strong>Encryption:</strong> {storage.storageConfig.encryptionEnabled ? 'Enabled' : 'Disabled'}</div>
                <div className="v2-card-row"><strong>Compression:</strong> {storage.storageConfig.compressionEnabled ? 'Enabled' : 'Disabled'}</div>
                <div className="v2-card-row"><strong>Retention:</strong> {storage.storageConfig.retentionDays} days</div>
            </div>

            <SectionTitle>Backups</SectionTitle>
            <table className="v2-table">
                <thead><tr><th>ID</th><th>Timestamp</th><th>Size (MB)</th><th>Type</th><th>Status</th></tr></thead>
                <tbody>
                    {storage.backups.map((b) => (
                        <tr key={b.id}>
                            <td className="v2-mono-sm">{b.id}</td>
                            <td>{new Date(b.timestamp).toLocaleString()}</td>
                            <td>{b.sizeMb}</td>
                            <td><Badge>{b.type}</Badge></td>
                            <td><Badge variant="stable">{b.status}</Badge></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── M8.7  Email + Invitation ────────────────────────────────────
const M8_7Panel = ({ invitations, teamMembers }) => {
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [sending, setSending] = useState(false);
    const [localInvites, setLocalInvites] = useState(invitations);

    const handleSend = async () => {
        if (!inviteEmail.trim()) return;
        setSending(true);
        const newInv = await api.sendInvitation({ email: inviteEmail, role: inviteRole });
        setLocalInvites((prev) => [newInv, ...prev]);
        setInviteEmail('');
        setSending(false);
    };

    return (
        <div className="v2-panel">
            <SectionTitle>Team Members</SectionTitle>
            <table className="v2-table">
                <thead><tr><th>Email</th><th>Role</th><th>Joined</th><th>Status</th></tr></thead>
                <tbody>
                    {teamMembers.map((m, i) => (
                        <tr key={i}>
                            <td>{m.email}</td>
                            <td><Badge variant={m.role === 'admin' ? 'plan' : 'default'}>{m.role}</Badge></td>
                            <td>{m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}</td>
                            <td><Badge variant={m.status === 'active' ? 'stable' : 'beta'}>{m.status}</Badge></td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <SectionTitle>Send Invitation</SectionTitle>
            <div className="v2-invite-row">
                <input className="v2-input" placeholder="Email address" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                <select className="v2-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                </select>
                <button className="v2-btn v2-btn-primary" onClick={handleSend} disabled={sending}>{sending ? 'Sending...' : 'Invite'}</button>
            </div>

            <SectionTitle>Invitations</SectionTitle>
            <table className="v2-table">
                <thead><tr><th>Email</th><th>Role</th><th>Invited</th><th>Status</th><th>Accepted</th></tr></thead>
                <tbody>
                    {localInvites.map((inv) => (
                        <tr key={inv.id}>
                            <td>{inv.email}</td>
                            <td><Badge>{inv.role}</Badge></td>
                            <td>{new Date(inv.invitedAt).toLocaleString()}</td>
                            <td><Badge variant={inv.status === 'accepted' ? 'stable' : inv.status === 'expired' ? 'error' : 'beta'}>{inv.status}</Badge></td>
                            <td>{inv.acceptedAt ? new Date(inv.acceptedAt).toLocaleString() : '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── Main Dashboard ──────────────────────────────────────────────
const V2Dashboard = ({ isOpen, closeV2Dashboard }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [data, setData] = useState({
        account: null, usage: null, endpoints: [], versions: [],
        searchResults: [], sdkInfo: null, certificates: [], signedPdfs: [],
        auditLog: [], storage: null, invitations: [], teamMembers: [],
    });

    useEffect(() => {
        if (!isOpen) return;
        const load = async () => {
            const [account, usage, endpoints, versions, searchResults, sdkInfo, certificates, signedPdfs, auditLog, storage, invitations, teamMembers] = await Promise.all([
                api.getAccount(), api.getUsageSnapshot(), api.getApiEndpoints(),
                api.getTemplateVersions(), api.searchLibrary(''), api.getSdkInfo(),
                api.getCertificates(), api.getSignedPdfs(), api.getAuditLog(),
                api.getStorageInfo(), api.getInvitations(), api.getTeamMembers(),
            ]);
            setData({ account, usage, endpoints, versions, searchResults, sdkInfo, certificates, signedPdfs, auditLog, storage, invitations, teamMembers });
        };
        load();
    }, [isOpen]);

    // ── Day 10: partial reload after a successful restore ─────────
    // M5Panel calls this when RestoreConfirm completes so the version
    // list reflects the newly created snapshot without re-fetching the
    // other panels' data.
    const refreshVersions = async () => {
        const v = await api.getTemplateVersions();
        setData((prev) => ({ ...prev, versions: v }));
    };

    // ── M7 Phase 4: partial reload after upload / set-default / revoke ─
    // Same pattern as refreshVersions; the M7Panel triggers it after
    // every write so the table reflects the new state without a full
    // dashboard reload.
    const refreshCertificates = async () => {
        const c = await api.getCertificates();
        setData((prev) => ({ ...prev, certificates: c }));
    };

    if (!isOpen) return null;

    const renderPanel = () => {
        switch (activeTab) {
            case 'overview':  return <OverviewPanel account={data.account} usage={data.usage} />;
            case 'm4':        return <M4Panel endpoints={data.endpoints} />;
            case 'm5':        return <M5Panel versions={data.versions} searchResults={data.searchResults} onVersionsRefresh={refreshVersions} />;
            case 'm6':        return <M6Panel sdkInfo={data.sdkInfo} />;
            case 'm7':        return <M7Panel certificates={data.certificates} signedPdfs={data.signedPdfs} onCertsRefresh={refreshCertificates} />;
            case 'm8':        return <M8Panel auditLog={data.auditLog} />;
            case 'm8_5':      return <M8_5Panel storage={data.storage} />;
            case 'm8_7':      return <M8_7Panel invitations={data.invitations} teamMembers={data.teamMembers} />;
            default:          return null;
        }
    };

    return (
        <div className="v2-overlay">
            <div className="v2-modal">
                <div className="v2-header">
                    <h2>WDoc Platform</h2>
                    <button className="v2-close-btn" onClick={closeV2Dashboard} title="Close">✕</button>
                </div>
                <div className="v2-tab-bar">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            className={`v2-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <span className="v2-tab-icon">{tab.icon}</span>
                            <span className="v2-tab-label">{tab.label}</span>
                        </button>
                    ))}
                </div>
                <div className="v2-content">
                    {renderPanel()}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    isOpen: state.v2Dashboard?.isOpen || false,
});

const mapDispatchToProps = { closeV2Dashboard };

export default connect(mapStateToProps, mapDispatchToProps)(V2Dashboard);
